/**
 * One-click "Add" from the add-players panel (SE-5490).
 *
 * ## Re-entry guard
 *
 * A double-clicked Add is the exact shape of the bug this repo has now hit
 * three times (SE-5494, SE-5504, SE-5500): two clicks land in the same tick,
 * both read the pre-update `isAdding` STATE, both pass the guard, and the
 * player is added twice. State is committed asynchronously, so it can never
 * gate a synchronous second call. The guard is therefore a `useRef` set of
 * in-flight player ids, written BEFORE the first `await`; `isAdding` exists
 * only to drive the spinner.
 *
 * ## Cache strategy — refetch, never a partial optimistic response
 *
 * `addSuggestedPlayerToPipeline` returns a bare `KanbanCard`, while the board's
 * card list normalizes on `PlayerWithKanban` (`ListKanbanCards`). An
 * `optimisticResponse` cast to bridge that gap is what produced the SE-5058
 * error-code-13 "Missing field" flood — an incomplete optimistic response is
 * worse than none. So there is none, and two reads are refetched instead:
 *
 * - `ListKanbanCards` — the board gains the card;
 * - `ListPipelineSuggestedPlayers` — the added player must LEAVE the Suggested
 *   list, and only the server can decide that (it excludes players carded in
 *   this pipeline). Filtering the row out locally would be the client
 *   re-deriving eligibility, which SE-5523 forbids.
 * @module features/player-kanban/hooks/useAddSuggestedPlayer
 */

import { useCallback, useMemo, useRef, useState } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import {
  type SuggestionReasonCategory,
  useAddSuggestedPlayerToPipelineMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

import { MESSAGES } from "../constants";

/** Board card list that must gain the new card. */
const LIST_KANBAN_CARDS_OP = "ListKanbanCards";
/** Recommendation read that must drop the now-carded player. */
const LIST_SUGGESTED_OP = "ListPipelineSuggestedPlayers";

/** Public API of {@link useAddSuggestedPlayer}. */
export interface UseAddSuggestedPlayerResult {
  /**
   * Add a player to the open pipeline. Resolves `false` without sending
   * anything when an add for that player is already in flight.
   */
  readonly addPlayer: (
    playerId: string,
    reasons?: readonly SuggestionReasonCategory[]
  ) => Promise<boolean>;
  /** Player ids with an add currently in flight, for per-row spinners. */
  readonly addingPlayerIds: readonly string[];
}

/**
 * Extract the server's message so backend copy reaches the user verbatim.
 * @param error - The thrown error.
 * @returns A display string.
 */
const resolveErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : MESSAGES.ERROR.ADD_PLAYER_FAILED;

/**
 * Add-to-pipeline for the suggested/search rows of the add-players panel.
 * @param kanbanBoardId - The open pipeline.
 * @returns The {@link UseAddSuggestedPlayerResult} imperative API.
 */
const useAddSuggestedPlayer = (
  kanbanBoardId: string | null | undefined
): UseAddSuggestedPlayerResult => {
  const toast = useToastNotification();
  const [addSuggestedPlayer] = useAddSuggestedPlayerToPipelineMutation();

  /**
   * The real guard. A ref, not state: it is readable and writable
   * SYNCHRONOUSLY, so a second click in the same tick sees the first click's
   * write.
   */
  const inFlightRef = useRef<Set<string>>(new Set());
  const [addingPlayerIds, setAddingPlayerIds] = useState<readonly string[]>([]);

  const addPlayer = useCallback(
    async (
      playerId: string,
      reasons?: readonly SuggestionReasonCategory[]
    ): Promise<boolean> => {
      if (!kanbanBoardId) return false;
      // Claim the id BEFORE any await. Everything below this line is
      // unreachable for a duplicate click.
      if (inFlightRef.current.has(playerId)) return false;
      inFlightRef.current.add(playerId);
      setAddingPlayerIds([...inFlightRef.current]);

      // SE-5508 `suggestion_acted`, add variant.
      //
      // Emitted here — immediately after the in-flight claim ADMITTED this
      // click, and before the mutation — for two reasons:
      //
      // 1. Exactly-once. A double-clicked Add returns at the guard above, so
      //    the second click can emit no more than it can add. Emitting from
      //    the row's press handler instead would count both clicks.
      // 2. `suggestion_acted` records the user's ACT, not its outcome (AC2:
      //    "when a user clicks Add or Dismiss"). Its success counterpart is
      //    `card_added`, emitted below only when the mutation committed — so a
      //    failed add yields one `suggestion_acted` and zero `card_added`.
      //
      // `reasons` is threaded down from the Suggested list, the only layer
      // holding the candidate; a `suggestionId`-less caller (the panel's
      // search rows) passes none and reports an empty string.
      void AnalyticsTracker.pipeline.logSuggestionActed({
        action: "add",
        reason_categories: (reasons ?? []).join(","),
        board_id: kanbanBoardId,
      });

      try {
        await addSuggestedPlayer({
          variables: { kanbanBoardId, playerId },
          // No optimisticResponse — see the module JSDoc.
          refetchQueries: [LIST_KANBAN_CARDS_OP, LIST_SUGGESTED_OP],
        });
        // SE-5508 `card_added`. Inside the `try`, after the awaited mutation
        // resolved, so a failed add (which falls to the `catch` and resolves
        // `false`) emits nothing.
        //
        // Exactly-once is inherited from the `inFlightRef` claim above rather
        // than re-implemented: a duplicate click returns before reaching this
        // mutation at all, so it can neither add twice nor emit twice.
        //
        // `stage` is "SERVER_DEFAULT" because `addSuggestedPlayerToPipeline`
        // sends no phase — the server picks the pipeline's entry stage, and
        // this hook discards the returned card. Guessing a stage id here would
        // be fabricated telemetry.
        void AnalyticsTracker.pipeline.logCardAdded({
          source: "suggested",
          stage: "SERVER_DEFAULT",
          board_id: kanbanBoardId,
        });
        toast.show({ action: "success", description: "Added to pipeline" });
        return true;
      } catch (error) {
        toast.show({
          action: "danger",
          description: resolveErrorMessage(error),
        });
        return false;
      } finally {
        inFlightRef.current.delete(playerId);
        setAddingPlayerIds([...inFlightRef.current]);
      }
    },
    [addSuggestedPlayer, kanbanBoardId, toast]
  );

  return useMemo(
    () => ({ addPlayer, addingPlayerIds }),
    [addPlayer, addingPlayerIds]
  );
};

export default useAddSuggestedPlayer;
